import { ActionIcon, Alert, Avatar, Badge, Box, Button, Card, Group, Pagination, Select, Skeleton, Stack, Table, Text, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconAlertTriangle, IconChevronRight, IconFilePlus, IconSearch, IconUsersGroup } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { BillingTenantApi } from "../../../apis";
import type { BillingTenantListResult, BillingTenantRow } from "../../../apis/billingTenant";
import { EmptyState } from "../../../components/billing/EmptyState";
import { MoneyCell } from "../../../components/billing/MoneyCell";
import { PermissionGate } from "../../../components/billing/PermissionGate";
import { PageLayout } from "../../../components/layout";

const breadcrumb = [
  { to: "/", label: "Эхлэл" },
  { to: "/billing", label: "Billing" },
  { label: "Тенантууд" },
];

const LIMIT = 25;

const REVENUE_MODEL_LABEL: Record<string, string> = {
  PLATFORM_SUBSCRIPTION: "Платформ захиалга",
  TENANT_FEE_BASED     : "Тенант шимтгэл",
  HYBRID               : "Хосолсон",
};

const STATUS_LABEL: Record<string, string> = {
  TRIALING : "Туршилт",
  ACTIVE   : "Идэвхтэй",
  PAST_DUE : "Хугацаа хэтэрсэн",
  SUSPENDED: "Түр зогссон",
  CANCELLED: "Цуцалсан",
};

const STATUS_COLOR: Record<string, string> = {
  TRIALING : "cyan",
  ACTIVE   : "green",
  PAST_DUE : "orange",
  SUSPENDED: "orange",
  CANCELLED: "red",
};

const FREQ_SUFFIX: Record<string, string> = {
  MONTHLY  : "/сар",
  QUARTERLY: "/улирал",
  ANNUAL   : "/жил",
};

const REFUND_LABEL: Record<string, string> = {
  PRO_RATA : "PRO_RATA",
  NONE     : "NONE",
  FULL_ONLY: "FULL_ONLY",
};

const REVENUE_MODEL_OPTIONS = Object.entries(REVENUE_MODEL_LABEL).map(([value, label]) => ({ value, label }));
const STATUS_OPTIONS = Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }));

// ── Cell renderers ──────────────────────────────────────────────────────────

function RevenueModelCell({ value }: { value: string | null }) {
  if (!value) return <Text c="dimmed" size="sm">—</Text>;
  // Revenue model is a taxonomy, not a status — one restrained neutral chip so the
  // eye goes to the semantic status badge instead.
  return <Badge color="gray" variant="light" radius="sm">{REVENUE_MODEL_LABEL[value] ?? value}</Badge>;
}

function ContractCell({ contract }: { contract: BillingTenantRow["contract"] }) {
  if (!contract || (!contract.recurringAmount && !contract.txFeeRate)) {
    return <Text c="dimmed" size="sm">—</Text>;
  }
  const suffix = contract.recurringFrequency ? FREQ_SUFFIX[contract.recurringFrequency] ?? "" : "";
  const txPct = contract.txFeeRate != null ? +(Number(contract.txFeeRate) * 100).toFixed(2) : null;
  return (
    <Group gap={6} wrap="nowrap">
      {contract.recurringAmount && (
        <Text span size="sm">
          <MoneyCell value={contract.recurringAmount} currency={contract.currency ?? "MNT"} />{suffix}
        </Text>
      )}
      {txPct != null && <Text span size="sm" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>+ {txPct}%</Text>}
    </Group>
  );
}

function FeeCell({ defaultFee }: { defaultFee: BillingTenantRow["defaultFee"] }) {
  if (!defaultFee) return <Text c="dimmed" size="sm">—</Text>;
  const refund = defaultFee.refundPolicy ? REFUND_LABEL[defaultFee.refundPolicy] ?? defaultFee.refundPolicy : "";
  if (defaultFee.calculationMethod === "FLAT") {
    return (
      <Text span size="sm">
        <MoneyCell value={defaultFee.flatFeeAmount ?? 0} />{refund ? ` ${refund}` : ""}
      </Text>
    );
  }
  if (defaultFee.calculationMethod === "PERCENTAGE" && defaultFee.feeRate != null) {
    const pct = +(Number(defaultFee.feeRate) * 100).toFixed(2);
    return <Text span size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>{pct}%{refund ? ` ${refund}` : ""}</Text>;
  }
  // TIERED or missing rate — show method + refund.
  return <Text span size="sm">{defaultFee.calculationMethod ?? "—"}{refund ? ` ${refund}` : ""}</Text>;
}

function StatusCell({ row }: { row: BillingTenantRow }) {
  if (row.status) {
    return <Badge color={STATUS_COLOR[row.status] ?? "gray"} variant="light">{STATUS_LABEL[row.status] ?? row.status}</Badge>;
  }
  // No billing account status → surface the most relevant gap as a warning.
  const warn = (label: string) => (
    <Badge color="orange" variant="light" radius="sm" leftSection={<IconAlertTriangle size={12} />}>{label}</Badge>
  );
  if (!row.hasActiveContract) return warn("Гэрээгүй");
  if (!row.hasDefaultPolicy) return warn("Бодлогогүй");
  return <Text c="dimmed" size="sm">—</Text>;
}

// ── Page ──────────────────────────────────────────────────────────────────────

const TenantsList = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [debouncedQ] = useDebouncedValue(q, 400);
  const [revenueModel, setRevenueModel] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  // Any search/filter change resets to page 1.
  useEffect(() => {
    setPage(1);
  }, [debouncedQ, revenueModel, statusFilter]);

  const swrKey = `swr.billing.tenants.${debouncedQ}.${revenueModel ?? ""}.${statusFilter ?? ""}.${page}`;
  const { data, error, isLoading, mutate } = useSWR(swrKey, () =>
    BillingTenantApi.listBillingTenants({
      search      : debouncedQ || undefined,
      revenueModel: revenueModel || undefined,
      status      : statusFilter || undefined,
      page,
      limit       : LIMIT,
    })
  );

  const result = data as BillingTenantListResult | undefined;
  const rows = result?.rows ?? [];
  const total = result?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const from = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const to = Math.min(page * LIMIT, total);

  return (
    <PageLayout title="Тенантууд" subTitle="Тенант сонгоод billing мэдээллийг нь харна" breadcrumb={breadcrumb}>
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="sm" wrap="wrap">
            <TextInput
              leftSection={<IconSearch size={16} />}
              placeholder="Тенантын нэрээр хайх…"
              value={q}
              onChange={(e) => setQ(e.currentTarget.value)}
              w={320}
            />
            <Select
              placeholder="Revenue model"
              data={REVENUE_MODEL_OPTIONS}
              value={revenueModel}
              onChange={setRevenueModel}
              clearable
              w={220}
            />
            <Select
              placeholder="Төлөв"
              data={STATUS_OPTIONS}
              value={statusFilter}
              onChange={setStatusFilter}
              clearable
              w={180}
            />
          </Group>
          <PermissionGate code="BIL_002">
            <Button
              leftSection={<IconFilePlus size={16} />}
              onClick={() => navigate("/billing/contracts/new")}
            >
              Шинэ гэрээ
            </Button>
          </PermissionGate>
        </Group>

        {isLoading ? (
          <Stack gap="xs">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} height={44} radius="sm" />
            ))}
          </Stack>
        ) : error ? (
          <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Жагсаалт ачаалахад алдаа гарлаа">
            <Stack gap="xs" align="flex-start">
              <Text size="sm">Дахин оролдоно уу.</Text>
              <Button size="xs" variant="light" color="red" onClick={() => mutate()}>
                Дахин ачаалах
              </Button>
            </Stack>
          </Alert>
        ) : rows.length === 0 ? (
          <Card withBorder radius="md" padding="md">
            <EmptyState
              icon={<IconUsersGroup size={26} />}
              message="Шүүлтүүрт тохирох тенант алга байна."
              action={(q || revenueModel || statusFilter) ? (
                <Button
                  size="xs"
                  variant="light"
                  color="brand"
                  onClick={() => { setQ(""); setRevenueModel(null); setStatusFilter(null); }}
                >
                  Шүүлтүүр цэвэрлэх
                </Button>
              ) : undefined}
            />
          </Card>
        ) : (
          <Card withBorder radius="md" padding={0}>
          <Table.ScrollContainer minWidth={720}>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Тенант</Table.Th>
                  <Table.Th>Revenue model</Table.Th>
                  <Table.Th>Идэвхтэй гэрээ</Table.Th>
                  <Table.Th>Тенант шимтгэл</Table.Th>
                  <Table.Th>Төлөв</Table.Th>
                  <Table.Th w={1} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((t) => (
                  <Table.Tr
                    key={t.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(`/billing/tenants/${t.id}`)}
                  >
                    <Table.Td>
                      <Group gap="sm" wrap="nowrap">
                        <Avatar color="brand" radius="md" size={34}>
                          {(t.name?.[0] ?? "?").toUpperCase()}
                        </Avatar>
                        <Box>
                          <Text fw={600}>{t.name}</Text>
                          <Text c="dimmed" size="xs">{t.domain ?? "—"}</Text>
                        </Box>
                      </Group>
                    </Table.Td>
                    <Table.Td><RevenueModelCell value={t.revenueModel} /></Table.Td>
                    <Table.Td><ContractCell contract={t.contract} /></Table.Td>
                    <Table.Td><FeeCell defaultFee={t.defaultFee} /></Table.Td>
                    <Table.Td><StatusCell row={t} /></Table.Td>
                    <Table.Td ta="right">
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        aria-label="Тенант нээх"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/billing/tenants/${t.id}`);
                        }}
                      >
                        <IconChevronRight size={18} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            </Table.ScrollContainer>

            <Group justify="space-between" px="md" py="sm">
              <Text c="dimmed" size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>{from}-{to} / {total}</Text>
              <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
            </Group>
          </Card>
        )}
      </Stack>
    </PageLayout>
  );
};

export default TenantsList;